/**
 * Console Logger - Captures console logs and sends to OverSkill for AI debugging
 *
 * This enables the AI to read actual browser console logs when debugging issues.
 * Only enabled when app is viewed in OverSkill editor iframe.
 */

import type { LogEntry, LogLevel, PlatformConfig, ConsoleLoggerInstance } from './types';

export class ConsoleLogger implements ConsoleLoggerInstance {
  private logs: LogEntry[] = [];
  private maxLogs = 100;
  private sendInterval = 5000; // 5 seconds
  private originalConsole: Record<string, (...args: unknown[]) => void> = {};
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private enabled = false;
  private isInIframe = false;
  private parentOrigin: string = '*'; // Will use '*' for cross-origin safety

  constructor(private config: PlatformConfig) {
    // Detect iframe status early
    this.isInIframe = typeof window !== 'undefined' && window.parent !== window && window.parent !== null;

    if (this.shouldEnable()) {
      this.enabled = true;
      this.init();
    }
  }

  /**
   * Determine if console logging should be enabled
   */
  private shouldEnable(): boolean {
    if (typeof window === 'undefined') return false;

    // CRITICAL: Only enable if in iframe (being viewed in OverSkill editor)
    // Don't run when app is viewed directly (wastes requests, causes errors)
    const isInIframe = window.parent !== window && window.parent !== null;

    if (!isInIframe) {
      this.debugLog('Not in iframe - skipping console logger');
      return false;
    }

    // Enable in development or if explicitly enabled
    const appConfig = window.APP_CONFIG || {};
    return (
      appConfig.ENVIRONMENT === 'development' ||
      appConfig.CONSOLE_LOGGING_ENABLED === true ||
      isInIframe // Always enable if in iframe for AI debugging
    );
  }

  /**
   * Initialize console interception
   */
  private init(): void {
    // Store original console methods
    this.originalConsole = {
      log: console.log.bind(console),
      error: console.error.bind(console),
      warn: console.warn.bind(console),
      info: console.info.bind(console),
      debug: console.debug.bind(console),
    };

    // Override console methods
    console.log = (...args: unknown[]) => this.captureLog('log', args);
    console.error = (...args: unknown[]) => this.captureLog('error', args);
    console.warn = (...args: unknown[]) => this.captureLog('warn', args);
    console.info = (...args: unknown[]) => this.captureLog('info', args);
    console.debug = (...args: unknown[]) => this.captureLog('debug', args);

    // Capture unhandled errors (also handled by ErrorReporter, but useful for log context)
    window.addEventListener('error', (event) => {
      this.captureLog('error', [
        `Uncaught ${event.error?.name || 'Error'}: ${event.message}`,
        `at ${event.filename}:${event.lineno}:${event.colno}`,
      ]);
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.captureLog('error', [`Unhandled Promise Rejection: ${event.reason}`]);
    });

    // Start periodic sending
    this.startPeriodicSend();

    this.debugLog('Console logging enabled via postMessage (iframe mode)');
  }

  /**
   * Capture a log entry
   */
  private captureLog(level: LogLevel, args: unknown[]): void {
    // Call original console method first
    this.originalConsole[level]?.(...args);

    // Capture log entry
    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message: this.formatArgs(args),
      appId: this.config.appId,
    };

    // Add to memory buffer
    this.logs.push(logEntry);

    // Keep only last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Send critical messages immediately (don't wait for batch interval)
    // - All errors: important for debugging
    // - Vite messages: CRITICAL for 502 cascade prevention!
    //   When "[vite] server connection lost" is detected, parent frame freezes iframe
    //   to stop the cascade. Must be sent BEFORE Vite triggers fallback reload.
    const messageStr = logEntry.message;
    const isViteMessage = messageStr.includes('[vite]');
    const is502Message = messageStr.includes('502') || messageStr.includes('Bad Gateway');

    if (level === 'error' || isViteMessage || is502Message) {
      this.sendLogsToServer();
    }
  }

  /**
   * Format arguments to string
   */
  private formatArgs(args: unknown[]): string {
    return args
      .map((arg) => {
        if (typeof arg === 'object' && arg !== null) {
          try {
            return JSON.stringify(arg, null, 2);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      })
      .join(' ');
  }

  /**
   * Start periodic log sending
   */
  private startPeriodicSend(): void {
    this.intervalId = setInterval(() => {
      if (this.logs.length > 0) {
        this.sendLogsToServer();
      }
    }, this.sendInterval);
  }

  /**
   * Send logs via postMessage to parent window (for iframe context)
   * This is the preferred method - works cross-origin without CORS issues
   */
  private sendLogsViaPostMessage(logsToSend: LogEntry[]): void {
    if (!this.isInIframe || !window.parent) return;

    logsToSend.forEach((log) => {
      try {
        // Format matches what console_log_capture_controller.js expects
        window.parent.postMessage(
          {
            type: 'console_log',
            log: {
              level: log.level,
              message: log.message,
              url: window.location.href,
              timestamp: log.timestamp,
              appId: log.appId,
            },
          },
          this.parentOrigin
        );
      } catch {
        // Silent fail - cross-origin postMessage should rarely fail
      }
    });

    this.debugLog(`Sent ${logsToSend.length} logs via postMessage`);
  }

  /**
   * Send logs to server via postMessage (iframe-only)
   *
   * NOTE: HTTP fallback was removed (Feb 2026) because:
   * 1. shouldEnable() already prevents initialization outside iframe
   * 2. Cross-origin HTTP to overskill.com from *.overskill.app always fails CORS
   * 3. The HTTP fallback was dead code that could only cause errors
   */
  private async sendLogsToServer(): Promise<void> {
    if (this.logs.length === 0) return;

    const logsToSend = [...this.logs];
    this.logs = []; // Clear after copying

    // Only send via postMessage when in iframe (cross-origin safe)
    if (this.isInIframe) {
      this.sendLogsViaPostMessage(logsToSend);
      return;
    }

    // Not in iframe - discard logs silently
    // This should never happen since shouldEnable() returns false outside iframe,
    // but guard defensively to prevent CORS errors
    this.debugLog('Not in iframe - discarding logs (no safe transport available)');
  }

  /**
   * Debug log helper (only when debug mode enabled)
   */
  private debugLog(message: string, data?: unknown): void {
    if (this.config.debug && this.originalConsole.log) {
      this.originalConsole.log(`[OverSkill Platform SDK] ${message}`, data || '');
    }
  }

  // Public API

  /**
   * Get captured logs
   */
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Clear captured logs
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Manually flush logs to server
   */
  async flush(): Promise<void> {
    await this.sendLogsToServer();
  }

  /**
   * Check if logger is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Cleanup (for testing)
   */
  destroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    // Restore original console methods
    if (this.originalConsole.log) {
      console.log = this.originalConsole.log;
      console.error = this.originalConsole.error;
      console.warn = this.originalConsole.warn;
      console.info = this.originalConsole.info;
      console.debug = this.originalConsole.debug;
    }
  }
}
